import type { FC } from 'react';
import React, { memo, useEffect, useMemo } from 'react';
import { Menu, Stack } from '@mui/material';
import {
  Button,
  Iconify,
  MenuItem,
  OneLineTypography,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import type {
  ReceiptBatchFormValues,
  TaxItem,
} from './ServiceProviderReceiptBatchForm';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';
import type { FormikErrors } from 'formik';

interface TaxMenuListCompositionProps {
  selectedTaxItem: TaxItem | undefined;
  handleTaxItemChange: (tax: TaxItem | undefined) => void;
  taxItems: TaxItem[];
  lineItemIndex?: number;
  index?: number;
  disabled?: boolean;
}

export const ServiceProviderReceiptBatchTaxSelection: FC<TaxMenuListCompositionProps> =
  memo(
    ({
      handleTaxItemChange,
      selectedTaxItem,
      taxItems,
      lineItemIndex,
      index,
      disabled,
    }) => {
      const validateFormik = useMemo(() => {
        return index !== undefined && lineItemIndex !== undefined;
      }, [index, lineItemIndex]);

      const [error, touched, setFieldTouched] = useReceiptBatchStore(
        useShallow((state) => [
          (
            (
              state?.formik?.errors?.receipts?.[index ?? 0] as FormikErrors<
                ReceiptBatchFormValues['receipts'][number]
              >
            )?.lineItems?.[lineItemIndex ?? 0] as FormikErrors<
              ReceiptBatchFormValues['receipts'][number]['lineItems'][number]
            >
          )?.taxes,
          state.formik?.touched?.receipts?.[index ?? 0]?.lineItems?.[
            lineItemIndex ?? 0
          ]?.taxes,
          state.formik?.setFieldTouched,
        ])
      );

      const handleClose = () => {
        if (validateFormik) {
          setFieldTouched?.(
            `receipts.${index}.lineItems.${lineItemIndex}.taxes`
          );
        }
      };

      return (
        <ServiceProviderReceiptBatchTaxSelectionView
          selectedTaxItem={selectedTaxItem}
          handleTaxItemChange={handleTaxItemChange}
          taxItems={taxItems}
          error={validateFormik ? error : undefined}
          touched={touched}
          handleClose={handleClose}
          disabled={disabled}
          index={index}
          lineItemIndex={lineItemIndex}
        />
      );
    }
  );

interface TaxMenuListCompositionViewProps<TTaxItem extends TaxItem> {
  selectedTaxItem: TTaxItem | undefined;
  handleTaxItemChange: (tax: TTaxItem | undefined) => void;
  taxItems: TTaxItem[];
  handleClose: () => void;
  error: string | undefined;
  touched: boolean | undefined;
  disabled?: boolean;
  index?: number;
  lineItemIndex?: number;
}

// eslint-disable-next-line complexity
const ServiceProviderReceiptBatchTaxSelectionViewBase = <
  TTaxItem extends TaxItem
>({
  handleTaxItemChange,
  selectedTaxItem,
  taxItems,
  error,
  touched,
  handleClose: handleCloseOriginal,
  disabled,
  lineItemIndex,
  index,
}: TaxMenuListCompositionViewProps<TTaxItem>): JSX.Element | null => {
  const { t } = useTranslation();

  const showError = useMemo(() => {
    return Boolean(error) && touched !== undefined;
  }, [error, touched]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    handleCloseOriginal();
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prevState) =>
      prevState === null ? event.currentTarget : null
    );
  };

  const handleSelect = (tax: TTaxItem | undefined) => {
    handleTaxItemChange(tax);
    setAnchorEl(null);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current && !open && anchorEl) {
      anchorEl.focus();
    }

    prevOpen.current = open;
  }, [anchorEl, open]);

  if (taxItems.length === 0) {
    return null;
  }

  const label = selectedTaxItem
    ? `${selectedTaxItem.taxName}${
        selectedTaxItem.hidePercentage
          ? ''
          : ` (${selectedTaxItem.taxAmountInPercentage}%)`
      }`
    : t('receipts.batch.form.tax-selection.select-tax');

  return (
    <Stack direction="row" spacing={2}>
      <div>
        <Button
          name={`receipts.${index}.lineItems.${lineItemIndex}.taxes`}
          variant="text"
          size="small"
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          startIcon={<Iconify icon={'mdi:pencil'} />}
          tabIndex={-1}
          color={showError ? 'error' : 'inherit'}
          disabled={disabled}
        >
          <OneLineTypography variant="body2">{label}</OneLineTypography>
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {taxItems.map((tax) => (
            <MenuItem key={tax.taxName} onClick={() => handleSelect(tax)}>
              {`${tax.taxName}${tax.paragraph ? `: ${tax.paragraph}` : ''}${
                tax.hidePercentage ? '' : ` (${tax.taxAmountInPercentage}%)`
              }`}
            </MenuItem>
          ))}
        </Menu>
      </div>
    </Stack>
  );
};

export const ServiceProviderReceiptBatchTaxSelectionView = memo(
  ServiceProviderReceiptBatchTaxSelectionViewBase
) as typeof ServiceProviderReceiptBatchTaxSelectionViewBase;
